// Single source of truth for the Sentry fields shared across every process —
// web client, web server, background worker, and scanner. Each process keeps
// only its genuinely-specific bits (SDK choice, integrations, sampling strategy,
// service tags); the identity core lives here so it can't drift between them.
//
// `env` is `process.env` on the server/worker/scanner and `window.env` in the
// browser — pass it in so this module stays isomorphic (no global env access,
// safe in the client bundle).

export function baseSentryConfig(env) {
  return {
    dsn: env.SENTRY_DSN,
    environment: env.APP_ENV,
    release: env.GIT_COMMIT_SHA || "development",
    sampleRate: 1, // errors: keep at 100% for visibility
  };
}

// Prisma internal spans are high-volume / low-value — dropped everywhere we
// touch the DB (web server, worker, scanner). Not used client-side.
export const IGNORE_PRISMA_SPANS = [/^prisma:/];

// --- Web trace tail sampling -------------------------------------------------
//
// Sentry has no built-in "send the trace only if it errored" option — trace
// sampling is head-based, decided before anything has happened. So the web
// client and server both record every trace locally (local spans cost nothing;
// quota is only consumed on send) and decide at send time in
// beforeSendTransaction:
//
//   1. a trace that produced an error is always kept in full;
//   2. otherwise a deterministic hash of the trace id keeps a small perf
//      stream. Hashing the trace id (rather than rolling a die) means the
//      browser and the server independently keep the SAME traces, so kept perf
//      traces are full-stack instead of half-missing.
//
// Known limit: an error thrown after its transaction already shipped or was
// dropped (e.g. a click handler minutes after pageload) can't resurrect those
// spans — the error event itself still always sends.

export const WEB_PERF_TRACE_KEEP_RATE = 0.1;
const MAX_REMEMBERED_ERRORED_TRACES = 1000;

export function isPerfKeptTraceId(traceId, rate = WEB_PERF_TRACE_KEEP_RATE) {
  if (!traceId) return false;

  const bucket = Number.parseInt(String(traceId).slice(0, 8), 16);
  if (!Number.isFinite(bucket)) return false;

  return bucket / 0x100000000 < rate;
}

export function createTraceTailSampler({
  perfKeepRate = WEB_PERF_TRACE_KEEP_RATE,
} = {}) {
  const erroredTraceIds = new Set();

  return {
    // beforeSend: note the trace id of every outgoing error event.
    rememberErroredTrace(event) {
      const traceId = event?.contexts?.trace?.trace_id;
      if (traceId) {
        erroredTraceIds.add(traceId);
        if (erroredTraceIds.size > MAX_REMEMBERED_ERRORED_TRACES) {
          const oldest = erroredTraceIds.values().next().value;
          erroredTraceIds.delete(oldest);
        }
      }
      return event;
    },

    // beforeSendTransaction: keep errored traces in full + the perf stream.
    keepTransaction(event) {
      const traceId = event?.contexts?.trace?.trace_id;
      if (traceId && erroredTraceIds.has(traceId)) return event;
      return isPerfKeptTraceId(traceId, perfKeepRate) ? event : null;
    },
  };
}
